/**
 * Day.js Configuration
 * 
 * Centralized configuration for day.js with i18n support
 */

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/en';
import 'dayjs/locale/ja';
import 'dayjs/locale/ko';
import 'dayjs/locale/fr';

// Load plugins
dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

// Default to Chinese locale
dayjs.locale('zh-cn');

/**
 * Set the locale for day.js
 * @param locale - 'zh', 'en', 'ja', 'ko', or 'fr'
 */
export function setDayjsLocale(locale: string) {
  if (locale === 'zh') {
    dayjs.locale('zh-cn');
  } else if (locale === 'en') {
    dayjs.locale('en');
  } else if (locale === 'ja') {
    dayjs.locale('ja');
  } else if (locale === 'ko') {
    dayjs.locale('ko');
  } else if (locale === 'fr') {
    dayjs.locale('fr');
  }
}

/**
 * Format date/time based on current locale
 * @param date - Date string or Date object
 * @param format - Optional custom format
 * @returns Formatted date string
 */
export function formatDateTime(date: string | Date, format?: string): string {
  if (!date) return '';
  
  const d = dayjs(date);
  
  // Use custom format if provided
  if (format) {
    return d.format(format);
  }
  
  // Auto format based on locale
  const locale = dayjs.locale();
  if (locale === 'zh-cn') {
    return d.format('YYYY年MM月DD日 HH:mm:ss');
  } else if (locale === 'ja') {
    return d.format('YYYY年MM月DD日 HH:mm:ss');
  } else if (locale === 'ko') {
    return d.format('YYYY년 MM월 DD일 HH:mm:ss');
  } else if (locale === 'fr') {
    return d.format('DD MMM YYYY HH:mm:ss');
  } else {
    return d.format('MMM DD, YYYY HH:mm:ss');
  }
}

/**
 * Format date only (without time)
 * @param date - Date string or Date object
 * @returns Formatted date string
 */
export function formatDate(date: string | Date): string {
  if (!date) return '';
  
  const d = dayjs(date);
  const locale = dayjs.locale();
  
  if (locale === 'zh-cn') {
    return d.format('YYYY年MM月DD日');
  } else if (locale === 'ja') {
    return d.format('YYYY年MM月DD日');
  } else if (locale === 'ko') {
    return d.format('YYYY년 MM월 DD일');
  } else if (locale === 'fr') {
    return d.format('DD MMM YYYY');
  } else {
    return d.format('MMM DD, YYYY');
  }
}

/**
 * Format time only (without date)
 * @param date - Date string or Date object
 * @returns Formatted time string
 */
export function formatTime(date: string | Date): string {
  if (!date) return '';
  return dayjs(date).format('HH:mm:ss');
}

/**
 * Format time for charts (short format)
 * @param date - Date string or Date object
 * @returns Formatted time string like "15:30"
 */
export function formatChartTime(date: string | Date): string {
  if (!date) return '';
  return dayjs(date).format('HH:mm');
}

/**
 * Format relative time (e.g., "2 hours ago", "2小时前")
 * @param date - Date string or Date object
 * @param withoutSuffix - If true, returns "2 hours" instead of "2 hours ago"
 * @returns Relative time string
 */
export function formatRelativeTime(date: string | Date, withoutSuffix = false): string {
  if (!date) return '';
  return dayjs(date).fromNow(withoutSuffix);
}

/**
 * Format relative time with threshold
 * If date is older than threshold days, return formatted date instead
 * @param date - Date string or Date object
 * @param thresholdDays - Days threshold (default: 7)
 * @returns Relative or absolute time string
 */
export function formatRelativeOrDate(date: string | Date, thresholdDays = 7): string {
  if (!date) return '';
  
  const d = dayjs(date);
  const now = dayjs();
  const diffDays = now.diff(d, 'day');
  
  if (diffDays < thresholdDays) {
    return d.fromNow();
  }
  
  return formatDate(date);
}

/**
 * Check if a date is today
 * @param date - Date string or Date object
 * @returns True if date is today
 */
export function isToday(date: string | Date): boolean {
  return dayjs(date).isSame(dayjs(), 'day');
}

/**
 * Check if a date is within last N days
 * @param date - Date string or Date object
 * @param days - Number of days
 * @returns True if date is within last N days
 */
export function isWithinDays(date: string | Date, days: number): boolean {
  const target = dayjs(date);
  const threshold = dayjs().subtract(days, 'day');
  return target.isAfter(threshold);
}

export default dayjs;

